import { createAction } from '@reduxjs/toolkit';
import {
    preplayToInplayEventNormalizer,
    preplayToInplayMarketsEventsNormalizer,
    preplayToInplaySelectionsAndDataPointersNormalizer,
    preplayToInplayUiMarkets,
} from '~shared/utils/preplayToInplayDataNormalisers';

/**
 * Finds all data pointers entity names associated with a given event ID.
 *
 * @param {string} preplayEventId - The ID of the event to search for.
 * @param {Object} stateDataPointers - The state object containing data pointers entities.
 * @returns {Array<string>} An array of entity IDs containing the event ID, or empty array if no matches are found.
 */
const findDataPointersEntityNameByEventId = (preplayEventId, stateDataPointers) => {
    const matchingEntities = [];
    const entities = Object.values(stateDataPointers);

    for (const entity of entities) {
        const hasEventId = entity.pointers.some(pointer => pointer.event_id === Number(preplayEventId));
        if (hasEventId) {
            matchingEntities.push(entity.id);
        }
    }

    return matchingEntities;
};

/**
 * Retrieves the market ID associated with a given preplay event ID.
 *
 * @param {Object} stateDataPointers - The state object containing existing data pointers entities.
 * @param {string} preplayEventId - The ID of the preplay event to find the market ID for.
 * @returns {number|undefined} The market ID associated with the preplay event, or `undefined` if not found.
 */
const getPreplayMarketId = (stateDataPointers, preplayEventId) => {
    // Create a flat list of pointers to filter those related to the preplay event.
    const flattenPointers = Object.values(stateDataPointers).flatMap(entity => entity.pointers);
    const prePlayEventPointers = flattenPointers.filter(pointer => pointer.event_id === Number(preplayEventId));

    return prePlayEventPointers[0]?.market_id;
};

/**
 * Updates all data pointers entities by replacing the preplay event ID with the inplay event ID
 * within the eventOrder list and appending new pointers to each entity. Returns a new object with
 * the updated data pointers.
 *
 * @param {number} inplayEventId - The ID of the inplay event to replace the preplay event ID.
 * @param {Array} pointers - An array of pointers to be added to the data pointers entities.
 * @param {Object} stateDataPointers - The state object containing state data pointers entities.
 * @param {Object} normalizedDataPointersUpdate - The state object containing updated data pointers entities.
 * @param {string} preplayEventId - The ID of the preplay event to be replaced.
 * @returns {Object} A new object containing the updated normalised data pointers.
 */
const updateDataPointersEntity = (inplayEventId, pointers, stateDataPointers, normalizedDataPointersUpdate, preplayEventId) => {
    const normalizedDataPointers = { ...normalizedDataPointersUpdate };
    const dataPointersEntities = findDataPointersEntityNameByEventId(preplayEventId, stateDataPointers);

    // Process each entity that contains the preplayEventId
    dataPointersEntities.forEach(entityName => {
        // Use existing eventOrder from normalizedDataPointers if it exists, otherwise use from stateDataPointers
        const currentEventOrder = normalizedDataPointers[entityName]
            ? normalizedDataPointers[entityName].eventOrder
            : stateDataPointers[entityName].eventOrder;

        const dataPointersEventOrder = currentEventOrder.map(eventId => {
            return eventId === Number(preplayEventId) ? inplayEventId : eventId;
        });

        // Add pointers to existing entity or create new one
        if (normalizedDataPointers[entityName]) {
            normalizedDataPointers[entityName].pointers.push(...pointers);
            normalizedDataPointers[entityName].eventOrder = dataPointersEventOrder;
        } else {
            normalizedDataPointers[entityName] = {
                eventOrder: dataPointersEventOrder,
                pointers: [...stateDataPointers[entityName].pointers, ...pointers],
            };
        }
    });

    return normalizedDataPointers;
};

/**
 * Handles the transition of events from preplay to inplay by normalising event, market, selection,
 * and data pointer information. Processes WebSocket data and updates the store with normalised data.
 *
 * @param {Object} params.wsData - The WebSocket data containing inplay event mappings and additional event info.
 * @param {Object} params.stateEvents - The current state of events in the store.
 * @param {Object} params.stateDataPointers - The current state of data pointers in the store.
 * @param {Object} params.stateMarketsEvents - The current state of marketsEvents in the store.
 * @param {Object} params.stateUiMarkets - The current state of uiMarkets in the store.
 * @returns {Object} The action payload containing normalised updates for events, marketsEvents, selections, and data pointers.
 */
export const eventInplayStart = createAction('eventInplayStart', ({
    wsData,
    stateEvents,
    stateDataPointers,
    stateUiMarkets,
}) => {
    const {
        extra_events_info: extraEventsInfo, // Inplay event data
        inplay_events: inplayEventsMap, // Preplay to inplay event mapping
    } = wsData;

    // All data normalisers prepared to be sent to the store
    const normalizedInPlayEventsUpdate = [];
    const normalizedMarketsEventsUpdate = [];
    const normalizedSelectionsUpdate = [];
    let normalizedDataPointersUpdate = {};
    let normalizedDataUiMarkets = {};

    // Process each inplay event
    Object.entries(inplayEventsMap).forEach(([preplayEventId, inplayEventId]) => {
        const extraEventInfo = extraEventsInfo[inplayEventId] || null;
        const stateEvent = stateEvents[preplayEventId] || {};
        const usesThreeMarketView = stateEvent.usesThreeMarketView;

        // Get preplay market ID
        const preplayMarketId = getPreplayMarketId(stateDataPointers, preplayEventId);

        // Normalise inplay event
        const inPlayEventData = preplayToInplayEventNormalizer(inplayEventId, extraEventInfo, stateEvent);
        normalizedInPlayEventsUpdate.push(inPlayEventData);

        // Normalise inplay markets event
        const marketsEventsData = preplayToInplayMarketsEventsNormalizer(
            inplayEventId,
            preplayMarketId,
            extraEventInfo,
            usesThreeMarketView
        );
        normalizedMarketsEventsUpdate.push(...marketsEventsData);

        // Normalise selections and pointers
        const { normalizedSelections, pointers } = preplayToInplaySelectionsAndDataPointersNormalizer(
                inplayEventId,
                preplayMarketId,
                extraEventInfo,
                usesThreeMarketView
            );
        normalizedSelectionsUpdate.push(...normalizedSelections);

        const dataPointersEntity = updateDataPointersEntity(
                inplayEventId,
                pointers,
                stateDataPointers,
                normalizedDataPointersUpdate,
                preplayEventId
            );
        normalizedDataPointersUpdate = {
            ...normalizedDataPointersUpdate,
            ...dataPointersEntity,
        };

        if (usesThreeMarketView) {
            const preplayUiMarkets = stateUiMarkets[`gameLineMarket-${preplayEventId}`];
            const dataUiMarkets = preplayToInplayUiMarkets(inplayEventId, preplayUiMarkets);
            normalizedDataUiMarkets = {
                ...normalizedDataUiMarkets,
                ...dataUiMarkets,
            };
        }
    });

    return {
        payload: {
            events: normalizedInPlayEventsUpdate,
            marketsEvents: normalizedMarketsEventsUpdate,
            selections: normalizedSelectionsUpdate,
            dataPointers: normalizedDataPointersUpdate,
            uiMarkets: normalizedDataUiMarkets,
        },
    };
});
